import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { useUserProfiles } from "../contexts/UserProfileContext";
import { Spinner } from "react-bootstrap";
import GameSelection from "../pages/GameSelection";

const ProtectedRoute = ({ children }) => {
  const { user, loading: authLoading } = useUser();
  const {
    activeProfile,
    profiles,
    isLoading: profileLoading,
  } = useUserProfiles();
  const location = useLocation();
  const [hasTimedOut, setHasTimedOut] = useState(false);

  // Add a timeout to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (authLoading || profileLoading) {
        console.log(
          "[ProtectedRoute] Loading timeout reached, forcing completion"
        );
        setHasTimedOut(true);
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeout);
  }, [authLoading, profileLoading]);

  // Reset timeout when loading states change
  useEffect(() => {
    if (!authLoading && !profileLoading) {
      setHasTimedOut(false);
    }
  }, [authLoading, profileLoading]);

  // Comprehensive logging for debugging
  console.log("[ProtectedRoute] DEBUG:", {
    timestamp: new Date().toISOString(),
    pathname: location.pathname,
    user: user ? { email: user.email, uid: user.uid } : null,
    authLoading,
    profileLoading,
    profiles: profiles ? profiles.length : 0,
    activeProfile: activeProfile
      ? { name: activeProfile.name, id: activeProfile.id }
      : null,
    hasTimedOut,
  });

  // Show loading spinner while checking auth or profiles (unless timed out)
  if ((authLoading || profileLoading) && !hasTimedOut) {
    console.log("[ProtectedRoute] SHOWING LOADING SPINNER", {
      authLoading,
      profileLoading,
      hasTimedOut,
      user: user ? 'present' : 'null',
      profilesCount: profiles?.length || 0
    });
    return (
      <div
        className="d-flex flex-column justify-content-center align-items-center"
        style={{
          height: "100vh",
          background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
        }}
      >
        <Spinner animation="border" role="status" style={{ color: "#ff69b4" }}>
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3" style={{ color: "#ffffff", opacity: 0.8 }}>
          Loading your profile...
        </p>
      </div>
    );
  }

  // If timed out, treat as if loading is complete
  if (hasTimedOut) {
    console.log(
      "[ProtectedRoute] Loading timed out, proceeding with current state"
    );
  }

  // Check if we're on any games route
  const isGamesRoute = location.pathname.startsWith("/games");

  // If not authenticated and trying to access /games, show the games list
  if (!user && location.pathname === "/games") {
    console.log(
      "[ProtectedRoute] SHOWING GAMESELECTION FOR UNAUTHENTICATED USER"
    );
    return <GameSelection />;
  }

  // If not authenticated and trying to access other protected routes, redirect to sign in
  if (!user) {
    console.log("[ProtectedRoute] REDIRECTING TO SIGNIN - NO USER");
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  // Special handling for games routes - allow access even without profiles for main games page
  // But require profiles for specific game routes
  if (isGamesRoute) {
    // For the main games page, allow access without profiles
    if (location.pathname === "/games") {
      console.log(
        "[ProtectedRoute] ALLOWING GAMES ACCESS - AUTHENTICATED USER"
      );
      return children;
    }

    // For specific game routes, require profiles and active profile
    if (profiles.length === 0) {
      console.log(
        "[ProtectedRoute] REDIRECTING TO SIGNIN - NO PROFILES FOR GAME ROUTE"
      );
      return <Navigate to="/signin" state={{ from: location }} replace />;
    }

    if (!activeProfile) {
      console.log(
        "[ProtectedRoute] REDIRECTING TO HOME - NO ACTIVE PROFILE FOR GAME ROUTE"
      );
      return <Navigate to="/" replace />;
    }

    console.log(
      "[ProtectedRoute] ALLOWING GAME ROUTE ACCESS - ALL CHECKS PASSED"
    );
    return children;
  }

  // For other protected routes, require profiles and active profile
  // If authenticated but has no profiles after waiting, redirect to signin
  if (profiles.length === 0) {
    console.log("[ProtectedRoute] REDIRECTING TO SIGNIN - NO PROFILES");
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  // If authenticated but no active profile, redirect to home to select profile
  if (!activeProfile) {
    console.log("[ProtectedRoute] REDIRECTING TO HOME - NO ACTIVE PROFILE");
    return <Navigate to="/" replace />;
  }

  console.log("[ProtectedRoute] RENDERING CHILDREN - ALL CHECKS PASSED");
  return children;
};

export default ProtectedRoute;
